import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	drawerTransitionProps,
	groupedSelectDividerStyles,
} from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import {
	CURRENCIES_GROUPED_OPTIONS,
	getCurrenciesGroupedOptionsExcluding,
} from '@dealstack/frontend/shared/currencies.ts';

import { CurrencyType } from '@dealstack/shared/lib/generated';
import {
	UPDATE_SECURITY_CURRENCY_FORM_FIELD,
	useUpdateSecurityCurrencyDrawer,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/UpdateSecurityCurrencyDrawer/useUpdateSecurityCurrencyDrawer.ts';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert.tsx';
import { UpdateSecurityCurrencyDrawerStyles } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/UpdateSecurityCurrencyDrawer/UpdateSecurityCurrencyDrawer.styles.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	refreshSecuritiesList: () => void;
	currentCurrency: CurrencyType | undefined;
	personName: string;
}

export const UpdateSecurityCurrencyDrawer = ({
	opened,
	onClose,
	refreshSecuritiesList,
	currentCurrency,
	personName,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const { updateSecurityCurrencyRequest, form, onSubmit } = useUpdateSecurityCurrencyDrawer(
		refreshSecuritiesList,
		onClose,
		currentCurrency
	);

	const newCurrencyOptions = getCurrenciesGroupedOptionsExcluding(currentCurrency);

	const closeDrawer = () => {
		form.reset();
		onClose();
	};

	return (
		<Drawer
			opened={opened}
			onClose={closeDrawer}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tCapManage('securities.currency_drawer.add_title')}
		>
			<UpdateSecurityCurrencyDrawerStyles>
				{updateSecurityCurrencyRequest.isPending && <InlineLoader />}

				<FormProvider {...form}>
					<form onSubmit={form.handleSubmit(onSubmit())}>
						<Alert icon="information-filled" state="warning" size="s">
							<AlertTitle>
								{tCapManage('securities.currency_drawer.warning.title')}
							</AlertTitle>

							<AlertDescription>
								{tCapManage('securities.currency_drawer.warning.description', {
									personName,
								})}
							</AlertDescription>
						</Alert>

						<Select
							name={UPDATE_SECURITY_CURRENCY_FORM_FIELD.currentCurrency}
							control={form.control}
							label={tCapManage('securities.currency_drawer.current_currency.label')}
							size="md"
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							data={CURRENCIES_GROUPED_OPTIONS}
							defaultValue={currentCurrency}
							disabled={true}
							styles={groupedSelectDividerStyles}
							data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_UPDATE_CURRENCY_OLD_FIELD}
						/>

						<Select
							name={UPDATE_SECURITY_CURRENCY_FORM_FIELD.newCurrency}
							control={form.control}
							label={tCapManage('securities.currency_drawer.new_currency.label')}
							size="md"
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							placeholder={tCapManage(
								'securities.currency_drawer.new_currency.placeholder'
							)}
							data={newCurrencyOptions}
							defaultValue={null}
							styles={groupedSelectDividerStyles}
							data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_UPDATE_CURRENCY_NEW_FIELD}
						/>

						<Button fullWidth mt="auto" type="submit">
							{tCapManage('securities.currency_drawer.create_button')}
						</Button>

						<Button onClick={closeDrawer} fullWidth variant="default">
							{tCapManage('securities.currency_drawer.close_button')}
						</Button>
					</form>
				</FormProvider>
			</UpdateSecurityCurrencyDrawerStyles>
		</Drawer>
	);
};
